import { Box, Button, Flex, Spacer, Text } from '@chakra-ui/react';
import { OrderActionType } from 'enums/modules/order/orderActionType';
import useOrderActionName from 'hooks/modules/order/useOrderActionName';
import useOrderExpirationText from 'hooks/modules/order/useOrderExpirationText';
import useOrderTimeLabel from 'hooks/modules/order/useOrderTimeLabel';
import useOrderTypeText from 'hooks/modules/order/useOrderTypeText';
import useAppTheme from 'hooks/shared/useAppTheme';
import useCurrencyText from 'hooks/shared/useCurrencyText';
import { OrderDetail } from 'model/modules/orders/orderDetail';
import moment from 'moment';
import AppCard from 'presentation/components/shared/card/AppCard';
import CardInfoRow from 'presentation/components/shared/card/CardInfoRow';
import { Color } from 'presentation/theme/colorScheme';
import { ButtonVariant } from 'presentation/theme/components/button';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

interface Props extends OrderDetail {
  onOrderCancelClick: () => void;
  onOrderConfirmClick: () => void;
}

const OrderDetailLoaded: FC<Props> = ({
  name,
  createdAt,
  id,
  action,
  ticker,
  status,
  type,
  amount,
  currency,
  openedPositionQuantity,
  quantity,
  expiration,
  isAllowCancel,
  isAllowConfirm,
  onOrderCancelClick: onOrderCancel,
  onOrderConfirmClick: onOrderConfirm,
  description,
}) => {
  const { t } = useTranslation();

  const actionName = useOrderActionName(action);

  const currencyFormatter = useCurrencyText();

  const typeText = useOrderTypeText(type);

  const volume = openedPositionQuantity * amount;

  const expirationText = useOrderExpirationText(expiration);

  const isBuyAction = [OrderActionType.Buy, OrderActionType.BuyMargin].includes(
    action
  );

  const orderTimeLabel = useOrderTimeLabel();

  const theme = useAppTheme();

  const statusColor = status.color ?? theme.colors[Color.Primary500];

  const infoRowWidth = 'calc(50% - 10px)';

  return (
    <AppCard>
      <Flex
        borderTopRadius="15px"
        justifyContent="space-between"
        px="20px"
        py="10px"
        bgColor={statusColor}
        h="100px"
      >
        <Text
          alignSelf="flex-end"
          noOfLines={2}
          fontSize={30}
          color="#fff"
          fontWeight={700}
        >
          {name}
        </Text>
        <Text
          noOfLines={1}
          textAlign="right"
          minW="60px"
          fontSize={24}
          color="#fff"
          fontWeight={700}
        >
          {orderTimeLabel(createdAt)}
        </Text>
      </Flex>
      <Box px="20px" pb="20px">
        <Flex flexWrap="wrap" justifyContent="space-between">
          <CardInfoRow
            labelProps={{ fontWeight: 700 }}
            width={infoRowWidth}
            label={isBuyAction ? t('buyOrderRequest') : t('sellOrderRequest')}
          >
            <Text fontWeight={400}>{id}</Text>
          </CardInfoRow>
          <CardInfoRow width={infoRowWidth} label={t('orderPrice')}>
            <Text>
              {currencyFormatter(amount, { symbol: currency.symbol })}
            </Text>
          </CardInfoRow>
          <CardInfoRow width={infoRowWidth} label={t('peFinanceHistoryType')}>
            <Text>{actionName}</Text>
          </CardInfoRow>
          <CardInfoRow width={infoRowWidth} label={t('orderQuantity')}>
            <Text>{quantity}</Text>
          </CardInfoRow>
          <CardInfoRow width={infoRowWidth} label={t('openedPositionsTableTicker')}>
            <Text>{ticker}</Text>
          </CardInfoRow>
          <CardInfoRow width={infoRowWidth} label={t('orderAmount')}>
            <Text>
              {currencyFormatter(volume, { symbol: currency.symbol })}
            </Text>
          </CardInfoRow>
          <CardInfoRow width={infoRowWidth} label={t('orderStatus')}>
            <Text color={Color.Primary500}>{status.description}</Text>
          </CardInfoRow>
          {expirationText && (
            <CardInfoRow width={infoRowWidth} label={t('orderExpiration')}>
              <Text>{expirationText}</Text>
            </CardInfoRow>
          )}
          {typeText && (
            <CardInfoRow width={infoRowWidth} label={t('orderType')}>
              <Text>{typeText}</Text>
            </CardInfoRow>
          )}
          <CardInfoRow width={infoRowWidth} label={t('orderCreatedAt')}>
            <Text>{moment(createdAt).format('L')}</Text>
          </CardInfoRow>
        </Flex>
        {description && <Text mt="10px">{description}</Text>}
        <Flex mt="20px">
        <Button
            flex="1"
            isDisabled={!isAllowCancel}
            variant={ButtonVariant.Secondary}
            onClick={onOrderCancel}
          >
            {t('declineAction')}
          </Button>
          <Spacer w="20px" maxW="20px" />
          <Button
            flex="1"
            isDisabled={!isAllowConfirm}
            onClick={onOrderConfirm}
          >
            {isBuyAction ? t('buy') : t('sell')}
          </Button>
        </Flex>
      </Box>
    </AppCard>
  );
};

export default OrderDetailLoaded;
